/**
 * Contenido de la guía de uso (`/help`, paso 8 del frontend).
 *
 * Es la ÚNICA pantalla del panel que no consume la API: todo lo que se lee acá sale de la
 * documentación del proyecto (`docs/`) y del código ya construido. Para que la guía no se
 * desincronice sola, nada que ya exista se escribe de nuevo:
 *   - las rutas y el ORDEN de las pantallas salen de `config/navigation.ts` y `routes/paths.ts`,
 *     así que una sección nueva del menú aparece en el índice sin tocar este archivo (con un aviso
 *     si todavía no tiene guía escrita);
 *   - las etiquetas y los colores de los estados salen de `theme/status.ts` (`orderStatus`,
 *     `orderStatusFlow`, `leadStatus`) y las de los roles, de `roleLabel`;
 *   - los significados, las transiciones y los requisitos son los de `docs/ORDENES.md`,
 *     `docs/CRM.md`, `docs/WHATSAPP_INTEGRACION.md`, `docs/INBOX.md`, `docs/PLANTILLAS.md`,
 *     `docs/REPORTES.md` y `docs/ONBOARDING_OAUTH.md`.
 *
 * Lo que todavía no existe se dice como PENDIENTE (TikTok, el sync con la Meta Ads API, la entrega
 * automática), nunca como si funcionara. No hay capturas ficticias
 * ni datos de ejemplo inventados: los números que se explican son reglas, no cifras.
 *
 * Es un `.ts` (sin JSX) a propósito: el texto se puede revisar y probar sin montar React; el
 * aspecto vive en `help-blocks.tsx`, `help-sections.tsx` y `HelpPage.tsx`.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  LayoutDashboard,
  MessageSquare,
  ShieldCheck,
  ShoppingCart,
  TriangleAlert,
  Truck,
  Users,
} from 'lucide-react';
import { navSections } from '@/config/navigation';
import { ROUTES } from '@/routes/paths';
import type { ToneName } from '@/theme';
import { orderStatusFlow } from '@/theme/status';
import type { EstadoLead, EstadoOrden, Role } from '@/types/domain';

/* ---------------------------------------------------------------------------
 * Secciones de la guía (el índice)
 * ------------------------------------------------------------------------- */

/** Parámetro de la URL que dice qué sección está abierta (el enlace se puede compartir). */
export const PARAM_SECCION = 'seccion';

export type SeccionId =
  | 'primeros-pasos'
  | 'pantallas'
  | 'whatsapp'
  | 'guia'
  | 'ordenes'
  | 'leads'
  | 'roles'
  | 'problemas';

export interface SeccionAyuda {
  id: SeccionId;
  titulo: string;
  /** Una línea para el índice. */
  bajada: string;
  icon: LucideIcon;
}

/** Las ocho secciones, en el orden en que se leen (de «por dónde empiezo» a «qué hago si falla»). */
export const SECCIONES: SeccionAyuda[] = [
  {
    id: 'primeros-pasos',
    titulo: 'Poner el negocio en marcha',
    bajada: 'El orden en que se configura todo, desde cero hasta el bot respondiendo.',
    icon: Building2,
  },
  {
    id: 'pantallas',
    titulo: 'Qué hace cada pantalla',
    bajada: 'Las secciones del menú, una por una, con su ruta y quién escribe en cada una.',
    icon: LayoutDashboard,
  },
  {
    id: 'whatsapp',
    titulo: 'WhatsApp de punta a punta',
    bajada: 'Requisitos en Meta, credenciales, webhook, fotos, audios y la ventana de 24 horas.',
    icon: MessageSquare,
  },
  {
    id: 'guia',
    titulo: 'La guía de envío marca el despacho',
    bajada: 'Subir la guía pasa la orden a ENVIADA y le avisa al cliente. Es irreversible.',
    icon: Truck,
  },
  {
    id: 'ordenes',
    titulo: 'Estados de una orden',
    bajada: 'Qué significa cada estado y hacia cuál se puede pasar.',
    icon: ShoppingCart,
  },
  {
    id: 'leads',
    titulo: 'Estados de un lead (CRM)',
    bajada: 'Frío, tibio, caliente y cliente: cómo se clasifica y quién lo puede mover.',
    icon: Users,
  },
  {
    id: 'roles',
    titulo: 'Quién puede hacer qué',
    bajada: 'Administrador y agente humano, pantalla por pantalla.',
    icon: ShieldCheck,
  },
  {
    id: 'problemas',
    titulo: 'Problemas típicos',
    bajada: 'Lo que más se pregunta, con su causa real y dónde se arregla.',
    icon: TriangleAlert,
  },
];

/** Sección abierta según la URL; si el valor no es una sección conocida, cae en la primera. */
export function seccionFrom(searchParams: URLSearchParams): SeccionId {
  const valor = searchParams.get(PARAM_SECCION);
  const encontrada = SECCIONES.find((seccion) => seccion.id === valor);
  return encontrada ? encontrada.id : SECCIONES[0].id;
}

/* ---------------------------------------------------------------------------
 * Poner el negocio en marcha (sección «primeros pasos»)
 * ------------------------------------------------------------------------- */

export interface PasoPuestaEnMarcha {
  titulo: string;
  detalle: string;
  /** Ruta de la pantalla donde se hace ese paso. */
  to: string;
  /** Texto del enlace. */
  cta: string;
}

/** El orden recomendado para configurar un negocio de cero (sigue el orden del plan del proyecto). */
export const PASOS_PUESTA_EN_MARCHA: PasoPuestaEnMarcha[] = [
  {
    titulo: 'Presenta el negocio',
    detalle:
      'Nombre y logo (se puede subir el archivo desde tu computador o pegar la URL de una imagen ya publicada), los nombres del asistente de ventas y del de postventa, la descripción y las políticas (envíos, devoluciones), el tono, el horario de atención humana y el número de WhatsApp del administrador, que es donde llegan los avisos internos. Revisa las reglas generales: son obligatorias y tienen prioridad sobre cualquier otra instrucción del bot.',
    to: ROUTES.settingsGeneral,
    cta: 'Abrir Ajustes → General',
  },
  {
    titulo: 'Carga los métodos de pago',
    detalle:
      'Cada método con sus datos (cuenta, titular) y sus condiciones («enviar comprobante antes de despachar», «solo disponible en Cali»). El bot ofrece únicamente los activos y responde con los datos exactos: al cerrar el pedido le manda al cliente la lista completa y le pide el comprobante del pago; cuando el comprobante llega, el bot lo lee (necesita Gemini), lo compara con el total y registra la venta con el medio detectado.',
    to: ROUTES.settingsPayments,
    cta: 'Abrir Ajustes → Métodos de pago',
  },
  {
    titulo: 'Conecta la inteligencia artificial',
    detalle:
      'La clave de DeepSeek es la que hace que el bot responda: sin ella no hay conversación. La de Gemini se usa para visión y para audios: identificar el producto de una foto que manda el cliente, **leer el comprobante de pago** (sin ella el bot no puede verificar el comprobante y el pedido queda marcado como «Por verificar») y transcribir las notas de voz. Si no vas a recibir fotos, comprobantes ni audios, Gemini puede esperar.',
    to: ROUTES.integrations,
    cta: 'Abrir Integraciones',
  },
  {
    titulo: 'Conecta WhatsApp',
    detalle:
      'Carga `phone_number_id`, `waba_id`, `access_token` y el `verify_token` del número en la tarjeta de WhatsApp, y configura el webhook en la app de Meta apuntando al backend. Hasta que esto no funcione, el negocio no recibe ni envía mensajes. El detalle completo está en «WhatsApp de punta a punta».',
    to: ROUTES.integrations,
    cta: 'Abrir Integraciones',
  },
  {
    titulo: 'Carga el catálogo',
    detalle:
      'Productos con fotos, precios por cantidad, inventario, las variantes que hay que preguntar en la orden y las FAQs. Esto es literalmente lo que el bot sabe: lo que no esté cargado no lo puede ofrecer, y lo que esté mal cargado se nota en la conversación.',
    to: ROUTES.products,
    cta: 'Abrir Productos',
  },
  {
    titulo: 'Deja listas las plantillas',
    detalle:
      'Fuera de la ventana de 24 horas solo se pueden mandar plantillas aprobadas por Meta. Siembra las recomendadas para los 9 eventos, ajústalas y envíalas a aprobación; las de encabezado de imagen se crean en WhatsApp Manager y se registran acá. Requiere el canal de WhatsApp conectado con `waba_id` y `access_token`.',
    to: ROUTES.templates,
    cta: 'Abrir Plantillas',
  },
  {
    titulo: 'Invita al equipo',
    detalle:
      'Suma a las personas que van a atender (el correo tiene que ser de una cuenta ya registrada) y decide quién es administrador: solo los administradores despachan, configuran y envían plantillas a Meta.',
    to: ROUTES.settingsTeam,
    cta: 'Abrir Ajustes → Equipo',
  },
  {
    titulo: 'Registra el gasto de tus anuncios',
    detalle:
      'El CPA se calcula con el gasto que se carga acá. «Descubrir anuncios» crea las filas de los anuncios y campañas que ya trajeron conversaciones, y luego completas el nombre y el monto. Sin gasto cargado el CPA se muestra «—», no como un cero.',
    to: ROUTES.campaigns,
    cta: 'Abrir Campañas',
  },
];

/** Lo que pasa después de la puesta en marcha (el día a día). */
export const RUTINA_DIARIA =
  'Con el negocio configurado, el día a día es: Conversaciones para atender y pausar el bot (handoff), Órdenes para confirmar y despachar, CRM para seguir el embudo y Panel o Reportes para mirar cómo va el periodo.';

/* ---------------------------------------------------------------------------
 * Las pantallas del panel (sección «Qué hace cada pantalla»)
 * ------------------------------------------------------------------------- */

export interface PantallaAyuda {
  /** Ruta real (clave de `ROUTES`), la misma del menú lateral. */
  to: string;
  /** Qué resuelve esta pantalla, en una frase. */
  resumen: string;
  /** Qué se hace ahí, punto por punto. */
  detalles: string[];
  /** Quién puede ESCRIBIR (leer lo puede hacer cualquier miembro del negocio). */
  escritura: { texto: string; tone: ToneName };
}

/** Texto que se repite en las pantallas donde nadie escribe nada. */
const SOLO_LECTURA: PantallaAyuda['escritura'] = {
  texto: 'Solo lectura: acá no se escribe nada',
  tone: 'neutral',
};

/**
 * Guía de cada pantalla, indexada por su ruta.
 *
 * El orden del índice no se define acá: sale de `navSections` (el menú lateral real), así que la
 * guía siempre se lee en el mismo orden en que se navega. Si una ruta del menú no tiene contenido,
 * la pantalla lo dice en vez de inventar algo.
 */
export const PANTALLAS: Record<string, PantallaAyuda> = {
  [ROUTES.dashboard]: {
    to: ROUTES.dashboard,
    resumen:
      'El resumen ejecutivo del negocio activo: cuánto entró, de dónde vinieron las conversaciones y a qué hora escribe la gente.',
    detalles: [
      'Cinco cifras del periodo (conversaciones nuevas, mensajes, órdenes creadas, ventas confirmadas e ingresos) con el detalle de cada una en una línea.',
      'Serie del periodo con el selector de métrica (conversaciones, mensajes, órdenes, ventas o ingresos) y de intervalo (día, semana o mes), más el embudo conversaciones → con orden → ventas.',
      'Publicidad y CPA: gasto atribuido y gasto registrado, CPA por anuncio/campaña y CPA global, con los avisos del backend cuando falta información.',
      'Mapa de calor 7×24 de los horarios en que escribe la gente y ranking de los cinco anuncios o campañas que más conversaciones traen.',
      'El periodo y la vista viven en la URL: el enlace del panel se puede compartir tal cual. Lo que se lee fila por fila está en Reportes.',
    ],
    escritura: SOLO_LECTURA,
  },

  [ROUTES.conversations]: {
    to: ROUTES.conversations,
    resumen:
      'La bandeja de los chats que entran por los canales, con el historial de cada conversación y el interruptor del bot.',
    detalles: [
      'Columna izquierda: bandeja con filtros en el servidor (canal, estado del lead, «requiere atención», orden y fechas) y búsqueda por el identificador del contacto; se refresca sola cada 30 segundos.',
      'Columna derecha: historial completo del chat agrupado por jornadas (los bloques JSON del pedido quedan colapsados), el estado del lead, el asistente activo, el anuncio/campaña de origen y las últimas órdenes del contacto.',
      'El interruptor del bot es el handoff: con el bot apagado, el mensaje del cliente NO se auto-responde y la conversación queda marcada como «requiere atención» hasta que alguien la atienda a mano.',
      'La conversación abierta vive en la URL (`?conversacion=`), así que se puede compartir el enlace a un chat concreto.',
    ],
    escritura: {
      texto: 'Solo ADMIN prende y apaga el bot (handoff)',
      tone: 'warning',
    },
  },

  [ROUTES.orders]: {
    to: ROUTES.orders,
    resumen:
      'El tablero del despacho: los pedidos que el bot cerró en el chat, con su historial de estados y la carga de la guía.',
    detalles: [
      'Listado con filtro de estado en el servidor y buscador por cliente, ciudad, dirección, teléfono, producto o número de guía sobre lo que ya está cargado (el endpoint no busca por texto).',
      'Ficha de la orden: productos con variantes y cantidades, total, dirección, método de pago, la guía cargada y el historial de cambios de estado con quién y cuándo.',
      'Cambio de estado solo por las transiciones permitidas: el backend rechaza una transición imposible con 400 y repetir el estado actual con 409.',
      'Carga de la guía de envío: es lo que marca el despacho y le avisa al cliente (ver «La guía de envío marca el despacho»).',
      'También se pueden cargar órdenes a mano; nacen en «Pendiente de confirmación» y hay que confirmarlas antes de despacharlas.',
    ],
    escritura: {
      texto: 'Solo ADMIN: crear, cambiar de estado y cargar la guía',
      tone: 'warning',
    },
  },

  [ROUTES.products]: {
    to: ROUTES.products,
    resumen:
      'El catálogo del negocio activo: es exactamente lo que el bot conoce cuando responde.',
    detalles: [
      'Por producto: nombre, descripción, fotos, tabla de precios por cantidad, inventario, las variantes que hay que preguntar en la orden (`datos_para_orden`), reglas especiales (obsequios, combos), FAQs y los `meta_ad_ids` de los anuncios que lo promocionan.',
      'La tabla resume lo que cada producto «aporta» al prompt (fotos, variantes, reglas, FAQs) y el interruptor de activo decide si el bot puede ofrecerlo.',
      'La búsqueda y el filtro de estado se resuelven en el cliente: el endpoint devuelve el catálogo entero, así que buscar es instantáneo.',
      'Un producto mal cargado se nota en la conversación: el bot improvisa el precio o no sabe qué variantes pedir. Las fotos se suben desde el panel (hasta 5 MB por archivo) o se pegan como dirección; tienen que quedar en una URL que el servidor pueda descargar, porque la visión (la que reconoce el producto en una foto del cliente) las lee desde ahí.',
    ],
    escritura: {
      texto: 'Solo ADMIN: crear, editar, activar/desactivar y borrar',
      tone: 'warning',
    },
  },

  [ROUTES.crm]: {
    to: ROUTES.crm,
    resumen: 'El embudo de leads del negocio: frío → tibio → caliente → cliente, en tablero y en tabla.',
    detalles: [
      'Un lead es una conversación (una por contacto y canal), y su estado vive en la conversación: el embudo y la bandeja del inbox son el mismo dato visto de dos maneras.',
      'Tablero con una columna por estado (cada una con su total real, así se ve cuando quedó corta) y tabla paginada del mismo lead con los mismos filtros; el buscador filtra en el cliente.',
      'Ficha del lead: las señales que encontró el clasificador, qué diría el clasificador hoy, el historial de reclasificaciones (quién, cuándo y por qué), las órdenes del contacto y sus últimos mensajes.',
      'La clasificación es automática y determinista (código puro, sin IA) al procesar cada mensaje entrante; cualquier miembro puede reclasificar a mano y eso deja el lead en MANUAL, donde ni los mensajes siguientes ni el recálculo masivo lo mueven.',
    ],
    escritura: {
      texto: 'Cualquier miembro reclasifica un lead; el recálculo masivo es solo ADMIN',
      tone: 'info',
    },
  },

  [ROUTES.campaigns]: {
    to: ROUTES.campaigns,
    resumen:
      'El gasto por anuncio o por campaña: es la única fuente del CPA que muestran el Panel y Reportes.',
    detalles: [
      'Una fila es una unidad de gasto: por anuncio (`meta_ad_id`, con la campaña opcional para agrupar) o por campaña entera (`meta_campaign_id`) cuando el gasto no se puede desglosar.',
      'Cada fila guarda nombre, moneda, gasto acumulado del periodo y un interruptor de activo: una fila inactiva se conserva pero sale de los reportes (útil para pausar el seguimiento o corregir una carga).',
      '«Descubrir anuncios» crea las filas que falten para los anuncios y campañas que ya aparecieron en las conversaciones, con gasto 0 y un nombre provisorio: el admin solo completa el nombre y el monto en vez de copiar identificadores a mano. Es idempotente.',
      'Acá no se calcula ninguna tasa a propósito: el CPA y la conversión se calculan en Reportes con estos números, para no tener dos versiones del mismo indicador.',
    ],
    escritura: {
      texto: 'Solo ADMIN: alta/edición, gasto, borrar y descubrir anuncios',
      tone: 'warning',
    },
  },

  [ROUTES.templates]: {
    to: ROUTES.templates,
    resumen:
      'Las plantillas aprobadas por Meta que se usan cuando la ventana de 24 horas con el cliente está cerrada.',
    detalles: [
      'Catálogo de los 9 eventos del sistema (cinco al cliente —confirmación, pedido enviado, recordatorio de pago, carrito abandonado y actualización de estado— y cuatro al administrador) con el texto sugerido, los campos disponibles y un botón para crear el borrador de ese evento.',
      'Tabla de las plantillas registradas: el nombre y el idioma EXACTOS con los que existen en el WABA, la categoría local y la de Meta, el estado real en Meta con su detalle, el mapeo de cada `{{n}}` a un campo del evento, el encabezado, la última sincronización y el interruptor de activo.',
      'El mapeo se mantiene solo al editar el cuerpo, y el backend exige que cubra exactamente las variables del texto: si sobra o falta una, la rechaza.',
      'Una plantilla que ya salió de borrador solo deja editar el mapeo y el interruptor: este módulo no propaga cambios de contenido a Meta.',
      'Las de encabezado de IMAGEN se crean en WhatsApp Manager y acá se registran con el mismo nombre e idioma, y se sincroniza su estado. Ojo: «Probar» es un envío REAL al número que indiques.',
    ],
    escritura: {
      texto: 'Solo ADMIN: crear, editar, borrar, sembrar, enviar a Meta, sincronizar y probar',
      tone: 'warning',
    },
  },

  [ROUTES.reports]: {
    to: ROUTES.reports,
    resumen:
      'Las seis tablas del módulo de reportes: el detalle que se audita, sobre los mismos filtros del Panel.',
    detalles: [
      'Los seis bloques: resumen del periodo (KPIs, conversaciones por canal, leads por estado y órdenes por estado), evolución punto por punto con sus totales, publicidad y CPA por anuncio o campaña, productos más vendidos, motivos de no cierre y horarios con las franjas pico.',
      'Los filtros (fechas, canal, anuncio, campaña) y la vista de cada bloque (métrica, intervalo, agrupación, orden y límite de productos, conteo del mapa) viven en la URL: el reporte que estás viendo es un enlace que se puede compartir.',
      'Un `null` del backend NO es un 0: el CPA, el ticket promedio y las tasas que no se pueden calcular se muestran «—» con la explicación. Los avisos y los truncados del backend se ven siempre, porque un reporte incompleto que no lo dice es peor que no tenerlo.',
      'El gasto atribuido y el gasto registrado son números distintos: lo que no se pudo atribuir a un anuncio aparece como su propia fila (`sin_atribucion`) en vez de esconderse.',
    ],
    escritura: SOLO_LECTURA,
  },

  [ROUTES.integrations]: {
    to: ROUTES.integrations,
    resumen: 'Las credenciales del negocio activo: las claves de inteligencia artificial y los canales de mensajería.',
    detalles: [
      'Inteligencia artificial: DeepSeek es el cerebro de los dos asistentes —sin su clave el bot no responde— y Gemini se usa para visión y para audios, es decir, para identificar a qué producto del catálogo corresponde la foto que manda un cliente y para transcribir las notas de voz que manda por WhatsApp.',
      'Canales: WhatsApp (el principal), Messenger e Instagram (los dos se pueden conectar con Facebook Login). TikTok no aparece porque no se va a usar por ahora: su API no está integrada y el backend solo guarda sus credenciales para cuando llegue.',
      'Cada tarjeta tiene conectar, probar conexión y desconectar. El canal de WhatsApp con `waba_id` y `access_token` es requisito para que las plantillas se puedan crear y enviar.',
      'Las claves y los tokens se guardan cifrados y por negocio, y no se vuelven a mostrar: la pantalla solo dice si están configurados, con una máscara.',
    ],
    escritura: {
      texto: 'Solo ADMIN: conectar, probar, desconectar y guardar las claves',
      tone: 'warning',
    },
  },

  [ROUTES.settings]: {
    to: ROUTES.settings,
    resumen:
      'Lo que el bot dice y cobra, en tres pestañas con ruta propia: General, Métodos de pago y Equipo.',
    detalles: [
      'General: nombre y logo del negocio (el logo se sube como archivo o se pega su URL), los nombres del asistente de ventas y del de postventa, descripción y políticas, tono/personalidad, horario de atención humana, número de WhatsApp del administrador (para las notificaciones internas) y las reglas generales obligatorias que el bot debe cumplir siempre.',
      'Métodos de pago: la lista de métodos con sus datos (cuenta, titular) y sus condiciones; el bot ofrece solo los activos y responde con los datos exactos, sin inventar ni mezclar información entre métodos.',
      'Equipo: los miembros del negocio con su rol, la invitación de un correo que ya tenga cuenta y las reglas de que no se puede degradar ni eliminar al último administrador.',
      'Cada pestaña tiene su propia URL (`/settings/general`, `/settings/payments`, `/settings/team`), así que se puede compartir el enlace de una pestaña concreta.',
    ],
    escritura: {
      texto: 'Solo ADMIN guarda General y Métodos de pago; Equipo entero es solo ADMIN',
      tone: 'warning',
    },
  },

  [ROUTES.help]: {
    to: ROUTES.help,
    resumen: 'Esta guía: cómo funciona el sistema y qué hace cada pantalla. No consume la API.',
    detalles: [
      'La sección abierta vive en la URL (`?seccion=`), así que se puede compartir el enlace directo a un tema.',
      'Todo lo que dice sale de la documentación del proyecto y del código construido; lo que todavía no existe se marca como pendiente en vez de contarse como si funcionara.',
    ],
    escritura: SOLO_LECTURA,
  },
};

export interface PantallaEnMenu {
  to: string;
  label: string;
  icon: LucideIcon;
  /** `null` si la ruta del menú todavía no tiene guía escrita (se avisa, no se inventa). */
  contenido: PantallaAyuda | null;
}

/** Las pantallas en el ORDEN del menú lateral real (una sola fuente: `navSections`). */
export function pantallasEnOrdenDelMenu(): PantallaEnMenu[] {
  return navSections
    .flatMap((seccion) => seccion.items)
    .map((item) => ({
      to: item.to,
      label: item.label,
      icon: item.icon,
      contenido: PANTALLAS[item.to] ?? null,
    }));
}

/* ---------------------------------------------------------------------------
 * WhatsApp de punta a punta (sección «whatsapp»)
 * ------------------------------------------------------------------------- */

export interface PasoAyuda {
  titulo: string;
  detalle: string;
}

/** El recorrido completo de un mensaje: requisitos en Meta, webhook, fotos y envío. */
export const PASOS_WHATSAPP: PasoAyuda[] = [
  {
    titulo: 'Reúne los datos del número en Meta',
    detalle:
      'De la app de Meta (WhatsApp → API Setup) salen el `phone_number_id` del número y el `waba_id` de la cuenta de WhatsApp Business; el `access_token` tiene que ser permanente (un System User si vas a producción). El `verify_token` lo eliges tú: es el valor con el que Meta valida tu webhook.',
  },
  {
    titulo: 'Carga las credenciales en Integraciones',
    detalle:
      'En la tarjeta de WhatsApp: `phone_number_id`, `waba_id`, `access_token` y `verify_token`. Se guardan cifradas y por negocio (nunca se comparten entre negocios) y la pantalla solo las muestra enmascaradas. «Probar conexión» avisa si algo no responde.',
  },
  {
    titulo: 'Configura el webhook en la app de Meta',
    detalle:
      'El Callback URL es el del backend terminando en `/api/webhooks/meta` y el Verify Token tiene que ser el mismo valor que cargaste en el canal. Meta hace primero una verificación con un GET (`hub.mode`, `hub.verify_token` y `hub.challenge`): el backend responde el challenge si el token coincide. Los mensajes entrantes llegan después por POST a esa misma dirección.',
  },
  {
    titulo: 'Cada mensaje se enruta por el número',
    detalle:
      'El webhook es uno solo para todos los negocios: lo que decide a quién pertenece el mensaje es el `phone_number_id` que viene en el payload. Nunca hay un negocio fijo, y ese es justo el motivo por el que los datos de un negocio no se cruzan con los de otro. Por eso el `phone_number_id` (y el `verify_token` que tú elijas) tampoco se pueden repetir en dos negocios: al guardar, el sistema lo comprueba y te dice cuál credencial ya está en uso en otro negocio.',
  },
  {
    titulo: 'De dónde sale la atribución de la venta',
    detalle:
      'Si el chat empezó en un anuncio «Click to WhatsApp», el mensaje trae un bloque `referral` con el anuncio de origen (`source_id`, que es el `ad_id`). Ese identificador viaja con la conversación y es lo que después permite atribuir conversaciones, ventas y CPA a un anuncio o a una campaña.',
  },
  {
    titulo: 'Las fotos se piden en dos pasos',
    detalle:
      'Cuando el cliente manda una imagen, el mensaje no trae la foto: trae un `image.id`. El sistema pide la dirección temporal de ese archivo, lo descarga y esa imagen es la que se manda al clasificador de visión (Gemini), que decide a qué producto del catálogo corresponde.',
  },
  {
    titulo: 'Los audios se escuchan y se transcriben',
    detalle:
      'Cuando el cliente manda una nota de voz, el mensaje trae un `audio.id` con su formato (`audio/ogg`). El sistema descarga ese audio y lo transcribe con Gemini, así que el bot lo entiende igual que si le hubieran escrito: puede responder precios, responder por una variante o incluso pasar a un asesor si el cliente lo pide por voz. En el Inbox ves la transcripción y la etiqueta «Audio». Si el negocio no tiene clave de Gemini cargada, el audio se guarda igual pero el bot responde pidiendo el mensaje en texto.',
  },
  {
    titulo: 'El bot responde y el negocio se entera',
    detalle:
      'La respuesta sale hacia la API de Meta usando el número del negocio. Además, los avisos internos llegan por WhatsApp al número del administrador configurado en Ajustes (nueva venta confirmada, pedido cancelado, atención humana requerida y JSON de pedido inválido).',
  },
  {
    titulo: 'Fuera de la ventana de 24 horas solo hay plantillas',
    detalle:
      'Pasadas 24 horas desde el último mensaje del cliente, WhatsApp solo acepta plantillas aprobadas. Por eso existen las Plantillas: si el texto libre falla porque la ventana está cerrada, el sistema reintenta con la plantilla del evento. Si esa plantilla no está aprobada y activa, el aviso queda registrado como fallido (el pedido igual se crea o se guarda).',
  },
];

/** Qué necesita cada cosa para funcionar (la tabla que resuelve la mitad de las dudas). */
export interface RequisitoAyuda {
  /** La capacidad, en una frase corta. */
  para: string;
  /** Qué hay que tener conectado o configurado. */
  necesita: string;
}

export const REQUISITOS_WHATSAPP: RequisitoAyuda[] = [
  {
    para: 'Recibir mensajes de los clientes',
    necesita:
      'El canal de WhatsApp conectado y el webhook de la app de Meta apuntando al backend, con el mismo Verify Token en los dos lados.',
  },
  {
    para: 'Que el bot responda',
    necesita:
      'La clave de DeepSeek en Integraciones (sin ella el bot no responde) y el canal de WhatsApp con su `access_token`.',
  },
  {
    para: 'Leer las fotos que mandan los clientes',
    necesita: 'La clave de Gemini y el producto cargado en el catálogo con sus fotos.',
  },
  {
    para: 'Entender los audios (notas de voz)',
    necesita:
      'La clave de Gemini (la misma que usa la visión). Sin ella el audio se guarda en el chat pero el bot no lo escucha y responde pidiendo el mensaje en texto.',
  },
  {
    para: 'Crear y enviar plantillas',
    necesita:
      'El canal de WhatsApp conectado con `waba_id` y `access_token`, y la plantilla aprobada y activa en Plantillas.',
  },
  {
    para: 'Avisarle al administrador',
    necesita:
      'El número del administrador en Ajustes → General y el canal conectado: dentro de la ventana de 24 horas el aviso es texto libre y fuera de ella necesita plantilla.',
  },
  {
    para: 'Calcular el CPA de un anuncio',
    necesita:
      'Que el chat venga de un anuncio con `referral` (para poder atribuirlo) y que ese anuncio tenga su gasto cargado en Campañas.',
  },
];

/** Lo que todavía está pendiente en los canales. */
export const NOTA_WHATSAPP_PENDIENTE =
  'Messenger e Instagram se pueden conectar con Facebook Login desde la misma tarjeta, pero el onboarding necesita que el backend tenga configurada la dirección de vuelta al panel: si no lo está, el botón muestra el aviso del backend y queda la carga manual de credenciales.';

/* ---------------------------------------------------------------------------
 * La guía de envío (sección «guia»)
 * ------------------------------------------------------------------------- */

/** El aviso que tiene que quedar claro antes de guardar: no se puede deshacer. */
export const AVISO_GUIA =
  'Cargar la guía ES el despacho y no se puede deshacer: en la misma operación la orden pasa a Enviada (con su fila en el historial, con tu usuario) y el cliente recibe en su chat la foto con el número de guía.';

export const PASOS_GUIA: PasoAyuda[] = [
  {
    titulo: 'Abre la orden en Órdenes',
    detalle:
      'El botón para cargar la guía aparece solo en las órdenes que la aceptan: Confirmada, En preparación y Enviada. En la ficha se ve el pedido completo (productos, total, dirección y método de pago) y el historial de estados.',
  },
  {
    titulo: 'Si está «Pendiente de confirmación», primero confirma la venta',
    detalle:
      'Las órdenes cargadas a mano nacen pendientes. El backend rechaza la guía en ese estado con un mensaje que pide pasar la orden a Confirmada o a En preparación: es a propósito, para que nadie despache algo que todavía no se confirmó.',
  },
  {
    titulo: 'Elige la foto y escribe el número',
    detalle:
      'Adjunta el archivo (JPG o PNG de hasta 5 MB, que se lee en tu equipo y viaja al backend) o pega la dirección pública que te dio la transportadora, y escribe el número de guía: ese número es el que va en el mensaje que recibe el cliente.',
  },
  {
    titulo: 'Guarda',
    detalle:
      'La orden pasa a Enviada (si no lo estaba), queda registrado quién la subió y cuándo, y el cliente recibe la foto con el número. Ese mensaje queda guardado en la conversación, así que aparece en el inbox como cualquier otro.',
  },
  {
    titulo: 'Si algo salió mal, vuelve a cargarla',
    detalle:
      'En una orden ya Enviada, cargar la guía otra vez sirve para reintentar el aviso o reemplazarla: queda en el historial como un reemplazo. Si el aviso no llegó por la ventana de 24 horas, el sistema reintenta con la plantilla de pedido enviado (si está aprobada y activa).',
  },
];

/** Estados que aceptan la guía (espejo de `canAttachGuide` de la pantalla de Órdenes). */
export const ESTADOS_QUE_ACEPTAN_GUIA: EstadoOrden[] = ['CONFIRMADA', 'EN_PREPARACION', 'ENVIADA'];

export interface MotivoEnvioAyuda {
  /** Valor de `envio.motivo` en la respuesta del backend (`null` cuando la foto salió). */
  motivo: string;
  significa: string;
  tone: ToneName;
}

/** Qué significa cada resultado del envío al cliente (los valores reales del módulo 7). */
export const MOTIVOS_ENVIO: MotivoEnvioAyuda[] = [
  {
    motivo: 'Enviada',
    significa: 'El cliente recibió la foto con el número de guía. Es el caso normal.',
    tone: 'success',
  },
  {
    motivo: 'foto_no_publica',
    significa:
      'La guía quedó guardada, pero su dirección no es pública porque al backend le falta configurar la URL base del servidor. Meta no pudo descargar la imagen, así que al cliente no le llegó: hay que configurar esa dirección y volver a cargar la guía.',
    tone: 'warning',
  },
  {
    motivo: 'sin_foto',
    significa: 'No había foto que enviar: se mandó solo el texto (por ejemplo, si cargaste únicamente el número).',
    tone: 'warning',
  },
  {
    motivo: 'envio_fallido',
    significa:
      'Meta rechazó el envío (lo más común: la ventana de 24 horas estaba cerrada y no había plantilla del evento aprobada). La orden igual quedó Enviada: conviene revisar la conversación.',
    tone: 'danger',
  },
  {
    motivo: 'sin_conversacion',
    significa:
      'La orden no tiene una conversación asociada (se cargó a mano desde el panel), así que no hay a quién avisarle por WhatsApp.',
    tone: 'neutral',
  },
];

/** Qué hacer si te equivocaste de guía. */
export const NOTA_GUIA_EQUIVOCADA =
  'El aviso al cliente no se puede deshacer. Si subiste la guía equivocada, vuelve a cargarla con la correcta (queda registrada como reemplazo) y avísale por el chat o con la plantilla de pedido enviado.';

/* ---------------------------------------------------------------------------
 * Estados de una orden (sección «ordenes»)
 * ------------------------------------------------------------------------- */

/** El flujo lineal, en orden (los colores y las etiquetas salen de `theme/status.ts`). */
export const FLUJO_ORDEN: EstadoOrden[] = orderStatusFlow;

export interface EstadoOrdenAyuda {
  estado: EstadoOrden;
  significado: string;
  /** A dónde puede pasar (las transiciones que el backend acepta). */
  hacia: EstadoOrden[];
  /** Quién lo hace en el día a día. */
  quien: string;
}

export const ESTADOS_ORDEN: EstadoOrdenAyuda[] = [
  {
    estado: 'PENDIENTE_CONFIRMACION',
    significado:
      'Pedido que todavía no está confirmado. Pasa por acá una orden cargada a mano y también una del chat cuando el comprobante del pago no se pudo verificar automáticamente (llegó ilegible): en ese caso revisá la imagen en el Inbox y, si el pago está bien, pasá la orden a CONFIRMADA. Con el comprobante leído y cuadrado la orden nace confirmada y no pasa por acá.',
    hacia: ['CONFIRMADA', 'CANCELADA'],
    quien: 'Solo ADMIN (confirmar la venta o cancelarla)',
  },
  {
    estado: 'CONFIRMADA',
    significado:
      'Venta confirmada. Es uno de los estados que cuentan como venta en el panel y en los reportes.',
    hacia: ['EN_PREPARACION', 'CANCELADA'],
    quien: 'Solo ADMIN',
  },
  {
    estado: 'EN_PREPARACION',
    significado: 'El pedido se está alistando para despachar.',
    hacia: ['ENVIADA', 'CANCELADA'],
    quien: 'Solo ADMIN',
  },
  {
    estado: 'ENVIADA',
    significado:
      'Salió a reparto. En el flujo normal se llega acá al cargar la guía de envío, no cambiando el estado a mano (igual se puede marcar a mano si hiciera falta).',
    hacia: ['ENTREGADA', 'CANCELADA'],
    quien: 'El sistema (al cargar la guía) o solo ADMIN a mano',
  },
  {
    estado: 'ENTREGADA',
    significado:
      'El cliente recibió el pedido. Hoy se marca a mano: la confirmación automática con la transportadora es una mejora pendiente, aunque el estado ya está listo para eso.',
    hacia: [],
    quien: 'Solo ADMIN a mano',
  },
  {
    estado: 'CANCELADA',
    significado:
      'Pedido cancelado desde cualquier estado que no sea final. Se le avisa al administrador, con el motivo si se escribió.',
    hacia: [],
    quien: 'Solo ADMIN',
  },
];

/** Los estados que cuentan como venta (los mismos que usa el módulo de reportes). */
export const ESTADOS_QUE_CUENTAN_COMO_VENTA: EstadoOrden[] = [
  'CONFIRMADA',
  'EN_PREPARACION',
  'ENVIADA',
  'ENTREGADA',
];

/** Detalles que no caben en la tabla de estados. */
export const NOTAS_ORDEN_ESTADOS: string[] = [
  'Cada cambio queda en el historial de la orden: qué estado había antes, a cuál pasó, con qué motivo, quién lo hizo y cuándo.',
  'Repetir el estado actual responde un aviso de conflicto (409) y una transición que no existe responde 400: el sistema no deja saltarse pasos.',
  'Una conversación puede tener varios pedidos activos: si el cliente vuelve a comprar en el mismo chat, el asistente de ventas lo atiende (vuelve solo desde postventa) y el bot de postventa ve todos sus pedidos a la vez.',
  'En los reportes, venta es Confirmada, En preparación, Enviada o Entregada. Pendiente de confirmación todavía no es una venta y Cancelada no lo es.',
];

/* ---------------------------------------------------------------------------
 * Estados de un lead (sección «leads»)
 * ------------------------------------------------------------------------- */

export interface EstadoLeadAyuda {
  estado: EstadoLead;
  significado: string;
  /** Qué lo distingue, en tres palabras (para la tarjeta). */
  clave: string;
}

export const ESTADOS_LEAD: EstadoLeadAyuda[] = [
  {
    estado: 'FRIO',
    significado:
      'Escribió y no mostró intención clara de compra (o solo saludó o preguntó precios) y desapareció antes de dar una cantidad.',
    clave: 'Interés sin cantidad',
  },
  {
    estado: 'TIBIO',
    significado:
      'Dio una cantidad o eligió una variante del catálogo: el interés ya está cuantificado, pero todavía no dejó los datos de envío.',
    clave: 'Cantidad o variante',
  },
  {
    estado: 'CALIENTE',
    significado:
      'Ya está entregando los datos del pedido (nombre, dirección, teléfono o correo): es el paso anterior al resumen.',
    clave: 'Datos de envío',
  },
  {
    estado: 'CLIENTE',
    significado:
      'Existe una orden de esa conversación, en cualquier estado (también cancelada). La conversación pasó a postventa.',
    clave: 'Hay una orden',
  },
];

export interface ReglaLeadAyuda {
  /** Condición de la regla (se aplica la primera que se cumple). */
  condicion: string;
  estado: EstadoLead;
  /** El motivo que queda guardado junto al estado. */
  motivo: string;
}

export const REGLAS_LEAD: ReglaLeadAyuda[] = [
  {
    condicion: 'La conversación tiene al menos una orden, en cualquier estado (también cancelada).',
    estado: 'CLIENTE',
    motivo: 'orden_creada',
  },
  {
    condicion:
      'Se detectan datos de envío: una palabra de dirección, un teléfono, un correo o «mi nombre es…».',
    estado: 'CALIENTE',
    motivo: 'datos_de_envio',
  },
  {
    condicion: 'Se detectó una cantidad (de 1 a 999) o una variante del catálogo.',
    estado: 'TIBIO',
    motivo: 'cantidad_o_variante',
  },
  {
    condicion: 'Solo se detectó interés: preguntó por precio, disponibilidad o información.',
    estado: 'FRIO',
    motivo: 'interes_sin_cantidad',
  },
  {
    condicion: 'Nada de lo anterior, pero hay mensajes de texto del contacto.',
    estado: 'FRIO',
    motivo: 'sin_senales_de_compra',
  },
  {
    condicion: 'Todavía no hay ningún mensaje entrante de texto o audio (por ejemplo, mandó solo una foto, o un audio que no se pudo transcribir).',
    estado: 'FRIO',
    motivo: 'sin_mensajes_entrantes',
  },
];

/** Cómo se calcula el estado de un lead. */
export const NOTAS_LEAD: string[] = [
  'La clasificación es automática y determinista: es código puro, no gasta tokens de IA y da el mismo resultado siempre. Corre cada vez que se procesa un mensaje del cliente.',
  'Mira los últimos 50 mensajes entrantes de texto de la conversación, el catálogo activo del negocio y si hay órdenes: es una heurística de palabras clave, así que puede equivocarse en los dos sentidos.',
  'Un lead reclasificado a mano queda en MANUAL: ni el mensaje siguiente ni el recálculo masivo lo mueven hasta que se lo devuelva a automático desde su ficha («Volver a automático»).',
  'El recálculo masivo sirve para las conversaciones que ya existían antes de activar el CRM o después de ajustar el catálogo; por defecto salta los leads que están en MANUAL.',
  'Un lead es una conversación por contacto y canal: el mismo cliente por WhatsApp y por Instagram son dos leads distintos.',
];

/* ---------------------------------------------------------------------------
 * Roles (sección «roles»)
 * ------------------------------------------------------------------------- */

export interface RolAyuda {
  rol: Role;
  quePuede: string;
}

export const ROLES: RolAyuda[] = [
  {
    rol: 'ADMIN',
    quePuede:
      'Configura el negocio y despacha. Es el único que puede guardar ajustes, conectar credenciales, escribir en el catálogo, cambiar el estado de una orden, cargar la guía, administrar plantillas, cargar gasto de campañas y hacer el recálculo masivo del CRM.',
  },
  {
    rol: 'AGENTE_HUMANO',
    quePuede:
      'Atiende. Ve todo lo que hay que atender (conversaciones, órdenes, catálogo, embudo de leads, panel y reportes) y puede clasificar un lead a mano, pero no cambia la configuración del negocio ni despacha.',
  },
];

export interface PermisoAyuda {
  pantalla: string;
  to: string;
  ver: string;
  escribir: { texto: string; tone: ToneName };
}

/** Quién puede hacer qué, pantalla por pantalla (leer = cualquier miembro del negocio). */
export const PERMISOS: PermisoAyuda[] = [
  {
    pantalla: 'Panel',
    to: ROUTES.dashboard,
    ver: 'Todos: indicadores, serie, embudo, publicidad y CPA, horarios y ranking.',
    escribir: { texto: 'Nada: es solo lectura.', tone: 'neutral' },
  },
  {
    pantalla: 'Conversaciones',
    to: ROUTES.conversations,
    ver: 'Todos: bandeja, historial del chat, estado del lead y órdenes del contacto.',
    escribir: { texto: 'Solo ADMIN prende y apaga el bot (handoff).', tone: 'warning' },
  },
  {
    pantalla: 'Órdenes',
    to: ROUTES.orders,
    ver: 'Todos: listado, ficha, historial de estados y la guía cargada.',
    escribir: {
      texto: 'Solo ADMIN crea órdenes, cambia el estado y carga la guía de envío.',
      tone: 'warning',
    },
  },
  {
    pantalla: 'Productos',
    to: ROUTES.products,
    ver: 'Todos: el catálogo completo y la ficha de cada producto.',
    escribir: {
      texto: 'Solo ADMIN crea, edita, activa/desactiva y borra.',
      tone: 'warning',
    },
  },
  {
    pantalla: 'CRM',
    to: ROUTES.crm,
    ver: 'Todos: tablero, tabla y ficha del lead (señales, historial y mensajes).',
    escribir: {
      texto:
        'Cualquier miembro reclasifica un lead a mano y lo devuelve a automático; el recálculo masivo es solo ADMIN.',
      tone: 'info',
    },
  },
  {
    pantalla: 'Campañas',
    to: ROUTES.campaigns,
    ver: 'Todos: el gasto es el mismo dato que usan el Panel y Reportes.',
    escribir: {
      texto: 'Solo ADMIN: alta/edición, actualizar el gasto, borrar y descubrir anuncios.',
      tone: 'warning',
    },
  },
  {
    pantalla: 'Plantillas',
    to: ROUTES.templates,
    ver: 'Todos: catálogo de eventos, listado de plantillas y previsualización.',
    escribir: {
      texto:
        'Solo ADMIN: crear, editar, borrar, sembrar, enviar a Meta, sincronizar y probar (la prueba es un envío real).',
      tone: 'warning',
    },
  },
  {
    pantalla: 'Reportes',
    to: ROUTES.reports,
    ver: 'Todos: las seis tablas, con los mismos filtros del Panel.',
    escribir: { texto: 'Nada: es solo lectura.', tone: 'neutral' },
  },
  {
    pantalla: 'Integraciones',
    to: ROUTES.integrations,
    ver: 'Todos: qué está configurado (las claves y los tokens nunca se muestran en claro).',
    escribir: {
      texto: 'Solo ADMIN: conectar, probar, desconectar y guardar claves.',
      tone: 'warning',
    },
  },
  {
    pantalla: 'Ajustes',
    to: ROUTES.settings,
    ver: 'General y Métodos de pago; la pestaña Equipo es solo ADMIN (el backend lo exige hasta para listar).',
    escribir: {
      texto: 'Solo ADMIN: guardar General y Métodos de pago, y administrar Equipo.',
      tone: 'warning',
    },
  },
  {
    pantalla: 'Ayuda',
    to: ROUTES.help,
    ver: 'Todos: esta guía.',
    escribir: { texto: 'Nada: es solo lectura.', tone: 'neutral' },
  },
];

export const NOTAS_ROLES: string[] = [
  'El rol es del negocio activo, no de la cuenta: el mismo usuario puede ser administrador en un negocio y agente humano en otro. Se cambia cambiando de negocio en el selector de la barra lateral.',
  'El backend es el que manda: cada ruta protegida responde 403 si al rol no le alcanza. El panel solo esconde los botones que van a fallar.',
  'La pestaña Equipo es la única pantalla completa solo-ADMIN; el resto se puede leer con cualquier rol.',
];

/* ---------------------------------------------------------------------------
 * Problemas típicos (sección «problemas»)
 * ------------------------------------------------------------------------- */

export interface PreguntaAyuda {
  pregunta: string;
  respuesta: string;
  /** Pantallas donde se resuelve (rutas reales). */
  enlaces: { label: string; to: string }[];
}

export const PROBLEMAS: PreguntaAyuda[] = [
  {
    pregunta: 'El bot no responde a los clientes',
    respuesta:
      'Casi siempre es una de dos cosas: falta la clave de DeepSeek (sin ella el bot no responde) o esa conversación tiene el bot pausado por un handoff. Los mensajes igual llegan y quedan guardados: no se pierden, solo dejan de contestarse solos.',
    enlaces: [
      { label: 'Integraciones', to: ROUTES.integrations },
      { label: 'Conversaciones', to: ROUTES.conversations },
    ],
  },
  {
    pregunta: 'No me llegan los avisos internos (nueva venta, cancelación, atención humana)',
    respuesta:
      'Los avisos al administrador son best-effort: necesitan el número del administrador cargado en Ajustes → General y el canal de WhatsApp conectado. El aviso es un mensaje de texto, así que solo llega dentro de la ventana de 24 horas de ese chat interno; fuera de ella hace falta la plantilla del evento aprobada y activa. Como es otra conversación, el aviso no queda guardado en el chat del cliente.',
    enlaces: [
      { label: 'Ajustes → General', to: ROUTES.settingsGeneral },
      { label: 'Plantillas', to: ROUTES.templates },
    ],
  },
  {
    pregunta: 'El cliente no recibió la foto de la guía',
    respuesta:
      'Mira el resultado que muestra el diálogo al guardar. Si dice foto_no_publica, al backend le falta configurar la dirección pública del servidor y Meta no pudo descargar la imagen: configúrala y vuelve a cargar la guía. Si dice envio_fallido, lo más probable es que la ventana de 24 horas estuviera cerrada y no hubiera plantilla de pedido enviado aprobada. En los dos casos la orden igual quedó Enviada.',
    enlaces: [
      { label: 'Órdenes', to: ROUTES.orders },
      { label: 'Plantillas', to: ROUTES.templates },
    ],
  },
  {
    pregunta: 'No puedo crear ni enviar plantillas a Meta',
    respuesta:
      'Para crear, sincronizar y enviar plantillas hace falta el canal de WhatsApp conectado con waba_id y access_token: si falta el WABA, el backend lo dice y hay que terminar esa conexión. Y las plantillas con encabezado de imagen se crean directamente en WhatsApp Manager: acá se registran con el mismo nombre e idioma y se sincroniza su estado.',
    enlaces: [
      { label: 'Integraciones', to: ROUTES.integrations },
      { label: 'Plantillas', to: ROUTES.templates },
    ],
  },
  {
    pregunta: 'El cliente mandó un audio y el bot no lo escuchó',
    respuesta:
      'El bot escucha las notas de voz con la clave de Gemini (la misma que usa la visión), así que lo primero es revisar que esté cargada en Integraciones: sin ella el audio se guarda igual en la conversación con la etiqueta «Audio», pero el bot responde pidiendo el mensaje en texto. Si la clave está puesta y aún así no lo entendió, puede ser un audio con puro ruido o música: en ese caso el bot pide que se lo repitan. La transcripción siempre queda guardada en el chat, así que puedes revisarla ahí.',
    enlaces: [
      { label: 'Integraciones', to: ROUTES.integrations },
      { label: 'Ayuda: WhatsApp de punta a punta', to: `${ROUTES.help}?${PARAM_SECCION}=whatsapp` },
    ],
  },
  {
    pregunta: 'Al conectar un canal me dice que ese phone_number_id ya está conectado en otro negocio',
    respuesta:
      'Es la comprobación que impide que dos negocios atiendan el mismo número: el webhook decide a quién pertenece cada mensaje por el phone_number_id (y por el page_id en Messenger e Instagram), así que ese valor solo puede estar en un canal activo. Si el número o la Page son tuyos, desconéctalos en el otro negocio o vuelve a conectarlos ahí con los datos correctos; si no lo son, revisa en Meta qué número tiene tu app. Lo mismo pasa con un verify_token elegido a mano que ya use otro negocio: el token de la app sí es común a todo el sistema, uno propio no.',
    enlaces: [
      { label: 'Integraciones', to: ROUTES.integrations },
      { label: 'Ayuda: WhatsApp de punta a punta', to: `${ROUTES.help}?${PARAM_SECCION}=whatsapp` },
    ],
  },
  {
    pregunta: 'El CPA sale «—» o los números del reporte no me cierran',
    respuesta:
      '«—» significa que no se puede calcular: falta el gasto del anuncio (se carga en Campañas) o no hay ventas confirmadas en el periodo, y un dato que no se puede calcular nunca se pinta como 0. Ojo también con las reglas: el periodo se aplica a la fecha de cada cosa (una venta cuenta por la fecha de la orden), venta es Confirmada, En preparación, Enviada o Entregada, y al filtrar por anuncio o campaña las órdenes cargadas a mano quedan fuera porque no tienen atribución.',
    enlaces: [
      { label: 'Campañas', to: ROUTES.campaigns },
      { label: 'Reportes', to: ROUTES.reports },
    ],
  },
  {
    pregunta: 'Un anuncio trajo conversaciones pero no aparece en Campañas',
    respuesta:
      'Usa «Descubrir anuncios»: crea las filas que falten para los anuncios y campañas que ya aparecieron en las conversaciones, con gasto 0 y un nombre provisorio, y después completas el nombre y el monto. Se puede correr las veces que haga falta, porque es idempotente.',
    enlaces: [{ label: 'Campañas', to: ROUTES.campaigns }],
  },
  {
    pregunta: 'Un lead no se mueve aunque el cliente siga escribiendo',
    respuesta:
      'Probablemente esté clasificado a mano: mientras esté en MANUAL, ni los mensajes siguientes ni el recálculo masivo lo cambian. En la ficha del lead se ve el origen del estado, el motivo y quién lo puso, y desde ahí se puede devolver a la clasificación automática.',
    enlaces: [{ label: 'CRM', to: ROUTES.crm }],
  },
  {
    pregunta: 'Subí la guía equivocada, o el pedido ya llegó y sigue en Enviada',
    respuesta:
      'La guía no se puede desenviar: vuelve a cargarla con la correcta (queda registrada como reemplazo en el historial) y avísale al cliente por el chat o con la plantilla de pedido enviado. Y «Entregada» hoy se marca a mano desde la orden: la confirmación automática con la transportadora todavía no existe.',
    enlaces: [{ label: 'Órdenes', to: ROUTES.orders }],
  },
  {
    pregunta: 'Cambié de negocio y no veo los datos que esperaba',
    respuesta:
      'Cada negocio tiene sus propios datos: conversaciones, catálogo, órdenes, equipo, credenciales, plantillas, campañas y reportes. El negocio activo se elige en el selector de la barra lateral, y el rol también es por negocio, así que al cambiar de negocio pueden cambiar los botones que ves.',
    enlaces: [{ label: 'Panel', to: ROUTES.dashboard }],
  },
  {
    pregunta: 'El buscador del header no hace nada',
    respuesta:
      'Es a propósito: todavía no existe una búsqueda global en el backend, así que el buscador del header y los iconos de mensajes y notificaciones quedaron como estructura deshabilitada, sin funcionalidad falsa. Para buscar, usa el buscador de cada pantalla: Órdenes, CRM, Productos, Plantillas y Campañas lo tienen, cada uno sobre los datos que carga.',
    enlaces: [{ label: 'Órdenes', to: ROUTES.orders }],
  },
];


